import './tripDetailsEdit.scss';

import { Card } from '@transferz/fe-component-library/src/components/card/Card';
import { ShipIcon } from '@transferz/fe-component-library/src/components/icons';
import CalendarIcon from '@transferz/fe-component-library/src/components/icons/CalendarIcon';
import CircleXIcon from '@transferz/fe-component-library/src/components/icons/CircleXIcon';
import ClockIcon from '@transferz/fe-component-library/src/components/icons/ClockIcon';
import LocationIcon from '@transferz/fe-component-library/src/components/icons/LocationIcon';
import { PlaneIcon } from '@transferz/fe-component-library/src/components/icons/PlaneIcon';
import PlaneLanding from '@transferz/fe-component-library/src/components/icons/PlaneLandingIcon';
import PlaneTakeOff from '@transferz/fe-component-library/src/components/icons/PlaneTakeOffIcon';
import { PointOfInterestIcon } from '@transferz/fe-component-library/src/components/icons/PointOfInterestIcon';
import { SeaportIcon } from '@transferz/fe-component-library/src/components/icons/SeaportIcon';
import { TrainIcon } from '@transferz/fe-component-library/src/components/icons/TrainIcon';
import Input from '@transferz/fe-component-library/src/components/input/Input';
import { HubType } from '@transferz/fe-models';
import { useLocalize } from 'localize-react';
import React, { ChangeEvent, useContext } from 'react';

import { useGoogleAddress } from '../../../../hooks/useGoogleAddress';
import { formatDate, formatTime } from '../../../../utils/utils';
import { FormContext } from '../../FormContext';

interface ITripDetailsEditProps {
  journey: any;
}

export const TripDetailsEdit: React.FunctionComponent<ITripDetailsEditProps> = ({
  journey,
}) => {
  const { translate } = useLocalize();
  const {
    placeSuggestions,
    showSuggestionContainer,
    setShowSuggestionContainer,
    handleSuggestionSelect,
    handleAddressChange,
    matchString,
  } = useGoogleAddress();
  const formattedDate = formatDate(journey?.journeyExecutionInfo?.pickupDate, 'YYYY-MM-DD');
  const formattedTime = formatTime(journey?.journeyExecutionInfo?.pickupDate, 'HH:mm');
  const { formData, formErrors, setFormData, setFormErrors } = useContext(FormContext);

  const setInputValue = (value: string, location: string) => {
    setFormData({
      ...formData,
      [location]: value,
    });
  };

  const handleSuggestionClick = (place: any, location: string) => {
    handleSuggestionSelect(place);
    setInputValue(place.description, location);
    setFormErrors({
      ...formErrors,
      [location]: false,
    });
  };

  const showPlaceSuggestions = (location: string) => {
    return placeSuggestions?.map((place: any) => {
      return (
        <div
          className="suggestion-item"
          key={place.place_id}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleSuggestionClick(place, location)}
        >
          <span dangerouslySetInnerHTML={matchString(place)}></span>
          <br />
          <span className="secondary-text">{place.structured_formatting.secondary_text}</span>
        </div>
      );
    });
  };

  const getHubIcon = (hubType: string, inbound: boolean) => {
    if (hubType === HubType.Airport) {
      return inbound ? <PlaneTakeOff width="23" height="28" /> : <PlaneLanding width="23" height="28" />;
    } else if (hubType === HubType.TrainStation) {
      return <TrainIcon />;
    } else if (hubType === HubType.Seaport) {
      return <SeaportIcon />;
    } else if (hubType === HubType.PointOfInterest) {
      return <PointOfInterestIcon />;
    }
  };

  const getVesselField = () => {
    const hubType = journey?.hub?.hubType;

    switch (hubType) {
    case HubType.Seaport:
      return <Input
          name="shipName"
          type="text"
          className="form-item"
          label={translate('booking_shipName')}
          iconLeft={<ShipIcon />}
          value={formData?.travellerInfo?.shipName || ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setFormData({
              ...formData,
              travellerInfo: {
                ...formData.travellerInfo,
                shipName: event.target.value,
              },
            });
            setFormErrors({
              ...formErrors,
              shipNameRequired: !journey.inbound && !event.target.value,
            });
          }}
          confirmed={formErrors?.shipNameRequired !== undefined}
          error={formErrors?.shipNameRequired
            ? translate('requiredField')
            : ''}
      />;
    case HubType.TrainStation:
      return <Input
          name="trainNumber"
          type="text"
          className="form-item"
          label={translate('booking_trainNumber')}
          iconLeft={<TrainIcon />}
          value={formData?.travellerInfo?.trainNumber || ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setFormData({
              ...formData,
              travellerInfo: {
                ...formData.travellerInfo,
                trainNumber: event.target.value,
              },
            });
          }}
      />;
    case HubType.Airport:
    default:
      return <Input
          name="flightNumber"
          type="text"
          className={'form-item rotate-icon'}
          label={translate('booking_flightNumber')}
          iconLeft={<PlaneIcon />}
          value={formData?.travellerInfo?.flightNumber || ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const flightNumberPattern = /^[A-Za-z]{1,3}[-\s]?[0-9]{1,5}$/i;
            setFormData({
              ...formData,
              travellerInfo: {
                ...formData.travellerInfo,
                flightNumber: event.target.value,
              },
            });
            setFormErrors({
              ...formErrors,
              flightNumber: !flightNumberPattern.test(event.target.value),
            });
          }}
          confirmed={formErrors?.flightNumber !== undefined}
          error={formErrors?.flightNumber
            ? translate(formData?.travellerInfo?.flightNumber.trim() === '' ? 'requiredField' : 'booking_invalidNumber')
            : ''}
      />;
    }
  };

  return (
    <div className="card-wrap">
      <Card title={translate('booking_tripDetails')} collapsed={false}>
        <div className="form-input google-input-block">
          <Input
            id="address-value"
            name="originLocation"
            nonEditable={journey?.originLocation?.hub}
            type="text"
            className={`form-item location-input ${journey?.originLocation?.hub ? 'rotate-icon' : ''} ${journey?.originLocation?.hub ? 'icon-position' : ''}`}
            label={`${translate('booking_from')} ${journey?.originLocation?.hub ? translate(`booking_${journey?.hub?.hubType}`) : translate('booking_address')}`}
            iconLeft={journey?.originLocation?.hub ? getHubIcon(journey?.hub?.hubType, journey?.inbound) : <LocationIcon />}
            value={formData?.originLocation || ''}
            autoComplete="off"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setInputValue(event.target.value, 'originLocation');
              setFormErrors({
                ...formErrors,
                originLocation: true,
              });
              handleAddressChange(event);
            }}
            onBlur={() => {
              setShowSuggestionContainer(false);
            }}
            confirmed={formErrors?.originLocation !== undefined}
            error={formErrors?.originLocation
              ? translate(formData?.originLocation?.trim() === '' ? 'requiredField' : 'booking_invalidAddress')
              : ''}
          />
          {showSuggestionContainer && !journey?.originLocation?.hub && (
            <div className="suggestion-container">
              {showPlaceSuggestions('originLocation')}
            </div>
          )}
          {(formData?.originLocation && formErrors?.originLocation) && (
            <span className="delete-button" onClick={() => {
              setInputValue('', 'originLocation');
              setFormErrors({
                ...formErrors,
                originLocation: true,
              });
            }}>
              <CircleXIcon width={15} />
            </span>
          )}
        </div>
        <div className="form-input google-input-block">
          <Input
            name="destinationLocation"
            type="text"
            nonEditable={journey?.destinationLocation?.hub}
            className={`form-item location-input ${journey?.destinationLocation?.hub ? 'rotate-icon' : ''} ${journey?.destinationLocation?.hub ? 'icon-position' : ''}`}
            label={`${translate('booking_to')} ${journey?.destinationLocation?.hub ? translate(`booking_${journey?.hub?.hubType}`) : translate('booking_address')}`}
            iconLeft={journey?.destinationLocation?.hub ? getHubIcon(journey?.hub?.hubType, journey?.inbound) : <LocationIcon />}
            value={formData?.destinationLocation || ''}
            autoComplete="off"
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setInputValue(event.target.value, 'destinationLocation');
              setFormErrors({
                ...formErrors,
                destinationLocation: true,
              });
              handleAddressChange(event);
            }}
            onBlur={() => {
              setShowSuggestionContainer(false);
            }}
            confirmed={formErrors?.destinationLocation !== undefined}
            error={formErrors?.destinationLocation
              ? translate(formData?.destinationLocation?.trim() === '' ? 'requiredField' : 'booking_invalidAddress')
              : ''}
          />
          {showSuggestionContainer && !journey?.destinationLocation?.hub && (
            <div className="suggestion-container">
              {showPlaceSuggestions('destinationLocation')}
            </div>
          )}
          {(formData?.destinationLocation && formErrors?.destinationLocation) && (
            <span className="delete-button" onClick={() => {
              setInputValue('', 'destinationLocation');
              setFormErrors({
                ...formErrors,
                destinationLocation: true,
              });
            }}>
              <CircleXIcon width={15} />
            </span>
          )}
        </div>
        <div className="form-input input-block-wrap">
          <div className="date-input">
            <Input
              name="date"
              type="custom-date"
              className="form-item"
              label={translate('booking_date')}
              iconLeft={<CalendarIcon />}
              defaultValue={formattedDate}
              onChange={(pickupDate: any) => {
                setFormData({
                  ...formData,
                  pickupDate,
                });
              }}
            />
          </div>
          <div className="time-input">
            <Input
              name="time"
              type="custom-time"
              className="form-item rotate-icon"
              label={translate('booking_time')}
              iconLeft={<ClockIcon />}
              defaultValue={formattedTime}
              onChange={(pickupTime: any) => {
                setFormData({
                  ...formData,
                  pickupTime,
                });
              }}
            />
          </div>
          <div className="flex-rows-break"></div>
          <div className="trip-number-input">{getVesselField()}</div>
        </div>
      </Card>
    </div>
  );
};
